import { PricingType, SanitizedWarrantySelection } from "@dune/types";
import { Checkbox, Loader } from "@dune/ui";
import { match } from "ts-pattern";
import { useMemo } from "react";
import { useEvaluateInitialPricing } from "../../../api/queries/constructionSite.queries";
import { usePricingOptions } from "./contexts/PricingOptionsContext";
import { useGetPricingByConstructionSiteAndBinderWarranty } from "../../../api/queries/constructionSiteOfferPricings.queries";
import { formatPricing, isOfferWarrantyType } from "@dune/utils";

/**
 * Affiche une offre à partir des données d'une garantie. Peut :
 * - Recalculer la tarification : si la props storedPricingType est undefined
 * - Prendre une tarification stockée : si la props storedPricingType est définie
 * Car grâce au constructionSiteId et les données liées au binderwarranty, on peut accéder à toutes les tarifications
 * calculées pour cette garantie sur ce chantier. Il ne manque donc plus qu'à savoir quelle étape de tarification on veut afficher.
 * Et c'est bien la props storedPricingType qui nous donne cette information.
 *
 * Donc si elle est définie, on récupère la tarification associée.
 * Sinon, on la recalcule.
 */

interface BinderWarrantyOfferContainerProps {
	binderWarranty: SanitizedWarrantySelection;
	constructionSiteId: string;
	storedPricingType?: PricingType;
	handleBinderWarrantyCheckboxChange?: (id: number) => void;
	checked?: boolean;
	isDisabled?: boolean;
	isSyndic?: boolean;
}

const BinderWarrantyOfferContainer = ({
	binderWarranty,
	constructionSiteId,
	storedPricingType,
	handleBinderWarrantyCheckboxChange,
	checked,
	isDisabled = false,
	isSyndic = false,
}: BinderWarrantyOfferContainerProps) => {
	const isChecked = checked ?? binderWarranty.checked;
	const warrantyType = binderWarranty.warrantyType;
	const binderAmendmentId = binderWarranty.binderAmendment.binder.id;

	const { getPricingOptionsForWarranty } = usePricingOptions();
	const pricingOptions = useMemo(
		() => getPricingOptionsForWarranty(binderWarranty.id),
		[getPricingOptionsForWarranty, binderWarranty.id],
	);

	const willEvaluate = !storedPricingType;

	const evaluateInitialPricingResult = useEvaluateInitialPricing(
		constructionSiteId,
		isOfferWarrantyType(warrantyType) ? warrantyType : null,
		binderAmendmentId,
		{
			...pricingOptions,
			returnMinimalOutput: true,
		},
		formatPricing,
		willEvaluate,
	);

	const {
		data: constructionSiteOfferPricingResult,
		isLoading: isLoadingConstructionSiteOfferPricing,
	} = useGetPricingByConstructionSiteAndBinderWarranty(
		Number(constructionSiteId),
		binderWarranty.id,
		storedPricingType,
	);

	const evaluatedPricing = match(evaluateInitialPricingResult)
		.with({ status: "success" }, ({ data }) => ({
			primeTTC: data.totalGrossPremium,
			brokerCommission: data.brokerCommission,
			brokerRemuneration: data.brokerRemuneration,
		}))
		.with({ status: "pending" }, () => ({
			primeTTC: "...",
			brokerCommission: "...",
			brokerRemuneration: "...",
		}))
		.with({ status: "error" }, () => ({
			primeTTC: "Error",
			brokerCommission: "Error",
			brokerRemuneration: "Error",
		}))
		.exhaustive();

	const storedPricing = {
		primeTTC: constructionSiteOfferPricingResult?.totalClientPrimeTTC
			? formatPricing(constructionSiteOfferPricingResult.totalClientPrimeTTC)
			: "...",
		brokerCommission: constructionSiteOfferPricingResult?.brokerCommission
			? formatPricing(constructionSiteOfferPricingResult.brokerCommission)
			: "...",
		brokerRemuneration: constructionSiteOfferPricingResult?.brokerRemuneration
			? formatPricing(constructionSiteOfferPricingResult.brokerRemuneration)
			: "...",
	};

	const { primeTTC, brokerCommission, brokerRemuneration } = willEvaluate
		? evaluatedPricing
		: storedPricing;

	return (
		<div
			className={`border-solid border-1 rounded-lg p-4 space-y-4 relative ${
				isChecked
					? "border-[#038EB7] bg-[#EBFDFF]"
					: isDisabled
						? "border-gray-200 bg-gray-50 opacity-50"
						: "border-gray-200"
			}`}
		>
			{isLoadingConstructionSiteOfferPricing ? (
				<Loader />
			) : (
				<>
					<Checkbox
						labelElement={
							<div
								className={`font-medium text-lg ${
									isChecked
										? "text-[#144C65]"
										: isDisabled
											? "text-gray-400"
											: ""
								}`}
							>
								Offre {binderWarranty.shortName ?? binderWarranty.name}
								{binderWarranty.dependentWarranties?.some(
									(dependantWarranty) => dependantWarranty.isMandatory,
								) && (
									<div className="text-xs font-normal">
										{"(incluant la "}
										{binderWarranty.dependentWarranties
											.filter(
												(dependentWarranty) => dependentWarranty.isMandatory,
											)
											.map(
												(dependentWarranty) => dependentWarranty.warrantyType,
											)
											.join(" et la ")}
										{")"}
									</div>
								)}
							</div>
						}
						id={`${String(binderWarranty.id)}-${binderWarranty.name}`}
						checked={isChecked}
						disabled={isDisabled}
						onCheckedChange={() => {
							if (
								!isDisabled &&
								!binderWarranty.isMandatory &&
								handleBinderWarrantyCheckboxChange
							) {
								handleBinderWarrantyCheckboxChange(binderWarranty.id);
							}
						}}
					/>
					<div className="flex flex-col gap-2 w-full">
						<div className="flex flex-row justify-between">
							<p className={isDisabled ? "text-gray-400" : ""}>Assureur</p>
							<p
								className={`text-sm font-medium ${
									isDisabled ? "text-gray-400" : "text-[#144C65]"
								}`}
							>
								{
									binderWarranty.binderAmendment?.binder?.insurer?.society
										?.companyName
								}
							</p>
						</div>
						<div className="flex flex-row justify-between">
							<p>Prime TTC</p>
							<p className="text-[#144C65] text-sm font-medium">{primeTTC}</p>
						</div>
						{!isSyndic && (
							<div className="flex flex-row justify-between">
								<p>Commission apporteur</p>
								<p className="text-[#144C65] text-sm font-medium">
									{brokerCommission}
								</p>
							</div>
						)}
						{!isSyndic && (
							<div className="flex flex-row justify-between">
								<p>Rémunération apporteur</p>
								<p className="text-[#144C65] text-sm font-medium">
									{brokerRemuneration}
								</p>
							</div>
						)}
					</div>
				</>
			)}
		</div>
	);
};

export default BinderWarrantyOfferContainer;
